import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { ApolloError } from '@apollo/client'

// eslint-disable-next-line camelcase
import {
  DoMenus_GetMenuGroupsGroup,
  DoMenus_GetMenuGroupsInput,
  DoMenus_GetMenuItemsItem,
  Menus_VisibilityEnum,
  useDoGetMenuGroupsPaginatedQuery,
  useDoGetMenuItemsQuery
} from 'src/apollo/onlineOrdering'
import { reportError } from 'src/lib/js/clientError'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

type BulkCallsContextType = {
  // eslint-disable-next-line camelcase
  menuItems: DoMenus_GetMenuItemsItem[]
  // eslint-disable-next-line camelcase
  menuGroups: DoMenus_GetMenuGroupsGroup[]
  loading: boolean
}

type BulkCallsContextProps = {
  bulkMenuItemIds?: any[] | null
  bulkMenuGroupIds?: any[] | null
}

export const BulkCallsContext = createContext<BulkCallsContextType | undefined>(
  undefined
)

// Provides the menu item and/or group details for the menu items and/or groups
// listed in the bulk calls section for this page.
export const BulkCallsContextProvider = ({
  bulkMenuItemIds,
  bulkMenuGroupIds,
  children
}: React.PropsWithChildren<BulkCallsContextProps>) => {
  const itemsToRequestList = useMemo(() => {
    return (
      bulkMenuItemIds?.map((item) => {
        return {
          menuGroupGuid: item?.itemGroupGuid,
          menuItemGuid: item?.itemGuid,
          restaurantGuid: item?.rxGuid,
          respectAvailability: false,
          // eslint-disable-next-line camelcase
          visibility: Menus_VisibilityEnum.ToastOnlineOrdering
        }
      }) || []
    )
  }, [bulkMenuItemIds])

  // eslint-disable-next-line camelcase
  const groupsToRequest: DoMenus_GetMenuGroupsInput = useMemo(() => {
    return {
      groups:
        bulkMenuGroupIds?.map(({ groupGuid, rxGuid, channelGuid }) => ({
          menuGroupGuid: groupGuid,
          restaurantGuid: rxGuid,
          channelGuid: channelGuid
        })) || [],
      respectAvailability: false,
      // eslint-disable-next-line camelcase
      visibility: Menus_VisibilityEnum.ToastOnlineOrdering
    }
  }, [bulkMenuGroupIds])

  const ooClient = useOOClient()

  const {
    data,
    error,
    loading: itemsLoading
  } = useDoGetMenuItemsQuery({
    variables: { input: { items: itemsToRequestList } },
    skip: itemsToRequestList.length === 0,
    client: ooClient
  })

  const menuItemsData: any[] = useMemo(() => {
    if (error) {
      reportError('Error fetching bulk menu items: ', error)
    }
    if (
      data &&
      data.doMenus_getMenuItems.__typename === 'DoMenus_GetMenuItemsResponse'
    ) {
      return data.doMenus_getMenuItems.items
    }
    return []
  }, [error, data])

  const { menuGroups: menuGroupsData, loading: menuGroupsLoading } =
    usePaginatedMenuGroups(groupsToRequest.groups, ooClient)

  const context = useMemo(() => {
    return {
      menuItems: menuItemsData,
      menuGroups: menuGroupsData,
      loading: itemsLoading || menuGroupsLoading
    }
  }, [menuGroupsData, menuItemsData, itemsLoading, menuGroupsLoading])

  return (
    <BulkCallsContext.Provider value={context}>
      {children}
    </BulkCallsContext.Provider>
  )
}

const usePaginatedMenuGroups = (
  groups: DoMenus_GetMenuGroupsInput['groups'],
  ooClient: ReturnType<typeof useOOClient>
): { menuGroups: any[]; loading: boolean } => {
  const input = useMemo(
    () => ({
      groups,
      respectAvailability: false,
      visibility: Menus_VisibilityEnum.ToastOnlineOrdering,
      offset: 0
    }),
    [groups]
  )

  const { data, error, loading, fetchMore } = useDoGetMenuGroupsPaginatedQuery({
    variables: { input },
    skip: groups.length === 0,
    notifyOnNetworkStatusChange: true,
    client: ooClient
  })

  const accumulatedResponse =
    data?.doMenus_getMenuGroupsPaginated.__typename ===
    'DoMenus_GetMenuGroupsPaginatedResponse'
      ? data.doMenus_getMenuGroupsPaginated
      : undefined

  const loadedItemsCount =
    accumulatedResponse?.groups.reduce(
      (count: number, group: DoMenus_GetMenuGroupsGroup | null) =>
        count + (group?.items.length ?? 0),
      0
    ) ?? 0

  const [fetchMoreError, setFetchMoreError] = useState<ApolloError>()
  useEffect(() => setFetchMoreError(undefined), [input])

  const hasMore =
    !loading &&
    !!accumulatedResponse &&
    loadedItemsCount < accumulatedResponse.totalCount

  useEffect(() => {
    if (!hasMore || fetchMoreError) return
    fetchMore({
      variables: { input: { ...input, offset: loadedItemsCount } },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        const prevPage = prev.doMenus_getMenuGroupsPaginated
        const incomingPage = fetchMoreResult.doMenus_getMenuGroupsPaginated
        if (
          prevPage.__typename !== 'DoMenus_GetMenuGroupsPaginatedResponse' ||
          incomingPage.__typename !== 'DoMenus_GetMenuGroupsPaginatedResponse'
        ) {
          return fetchMoreResult
        }
        const mergedGroups = incomingPage.groups.map((incomingGroup, index) => {
          const existingGroup = prevPage.groups[index]
          if (!existingGroup || !incomingGroup) return incomingGroup
          return {
            // Spread is safe: existing and incoming are == besides items
            ...incomingGroup,
            items: [...existingGroup.items, ...incomingGroup.items]
          }
        })
        return {
          ...fetchMoreResult,
          doMenus_getMenuGroupsPaginated: {
            ...incomingPage,
            groups: mergedGroups
          }
        }
      }
    }).catch((error: ApolloError) => {
      setFetchMoreError(error)
      reportError('Error paging bulk menu groups: ', error)
    })
  }, [hasMore, fetchMoreError, loadedItemsCount, fetchMore, input])

  const menuGroups = useMemo(() => {
    if (error) {
      reportError('Error fetching bulk menu groups: ', error)
    } else if (
      data?.doMenus_getMenuGroupsPaginated.__typename ===
      'DoMenus_GetMenuGroupsPaginatedError'
    ) {
      reportError(
        'Error fetching bulk menu groups: ',
        new Error(data.doMenus_getMenuGroupsPaginated.message)
      )
    }
    return accumulatedResponse?.groups ?? []
  }, [accumulatedResponse, error, data])

  return { menuGroups, loading: loading && menuGroups.length === 0 }
}

export const useBulkCalls = () => {
  const context = useContext(BulkCallsContext)
  if (!context) {
    throw new Error(
      'useBulkCalls must be used within a BulkCallsContextProvider'
    )
  }

  return context
}
